import moment from 'moment';

import { get, startCase, isEmpty } from 'lodash-es';

import numeral from './numeral';
import {
  FILE_PREVIEW_TYPE,
  TOTAL_SECONDS_IN_HOUR,
  TOTAL_SECONDS_IN_DAY,
  TOTAL_MINUTE_IN_HOUR,
  SHIPMENT_STATUSES
} from './constants';

/**
 * Process object of date range to match with backend filter format.
 * @param {Object} params
 * @param {String} params.start
 * @param {String} params.end
 */
export function formatDateRangeToBackendDateRangeFilter({ start, end }) {
  return {
    start: moment(start).toISOString(),
    end: moment(end).toISOString()
  };
}

export function formatDateTimeRangeToBackendDateRangeFilter({ start, end }) {
  return {
    start: moment(start).startOf('day').toISOString(),
    end: moment(end).endOf('day').toISOString()
  };
}

/**
 * Process array of string to match with backend filter format.
 * Empty array will be converted to undefined
 * @param {*} listOfString
 */
export function formatArrayOfStringForBackendFilter(listOfString) {
  if (!listOfString || (Array.isArray(listOfString) && listOfString.length < 1)) {
    return undefined;
  }
  return listOfString;
}

/**
 * Process array of string and array to match with backend filter format.
 * Empty array will be converted to undefined
 * @param {*} listOfString
 */
export function formatArrayOfStringArrayForBackendFilter(listOfString) {
  if (!listOfString || (Array.isArray(listOfString) && listOfString.length < 1)) {
    return undefined;
  }
  if (typeof (listOfString[0]) === 'string') {
    return listOfString;
  }
  return [...listOfString[0]];
}

/**
 * Returns number with percentage format
 * @param {number} number
 * @returns {string}
 */
export function percentageFormat(number) {
  if (number === undefined || number === null || number === '' || number === '-') return number;
  return numeral(number).format('0,0.[00]%');
}

/**
 * Returns number with currency format
 * @param {number} number
 */
export function currencyFormat(number, currency, showSymbol = true) {
  if (number === undefined || number === null || number === '' || number === '-') return number;

  const curr = currency || 'IDR';

  let format = '';
  if (showSymbol) format += '$'

  if (curr === 'IDR') format += '0,0'
  else format += '0,0.00'

  return numeral(Number(number), curr).format(format);
}

/**
 * Returns number with currency format,
 * but always in negative
 * @param {number} number
 */
export function currencyFormatNegative(number, currency) {
  return numeral(-(Math.abs(number) || 0), currency || 'IDR').format('$0,0[.]00');
}

/**
 * Returns a plain number from a currency-formatted string
 * @param {String} currencyNumber
 */
export function currencyNormalize(currencyNumber) {
  if (currencyNumber === '-') return currencyNumber;
  return numeral(currencyNumber).value();
}

/**
 * Returns a plain number from a currency-formatted string,
 * but always in negative
 * @param {String} currencyNumber
 */
export function currencyNormalizeNegative(currencyNumber) {
  return -Math.abs(numeral(currencyNumber).value());
}

/**
 * Returns a plain number from a currency-formatted string,
 * but always in positive
 * @param {String} currencyNumber
 */
export function currencyNormalizePositive(currencyNumber) {
  return Math.abs(numeral(currencyNumber).value());
}

/**
 * Returns number with currency format,
 * but negative value will have parantheses instead of minus sign
 * @param {number} number
 */
export function currencyFormatWithParantheses(number, currencyInside = false, currency) {
  let format = '';
  const curr = currency || 'IDR';

  if (currencyInside) {
    format += '($';
  } else {
    format += '$(';
  }
  if (curr === 'IDR') {
    format += '0,0[.]00)'
  } else {
    format += '0,0.00)'
  }

  return numeral(number, currency).format(format);
}

/**
 * Returns an ordinal format of given number
 * @param {*} number
 * @param {*} locale
 */
export function ordinalNumber(number, locale = 'id') {
  if (locale === 'id') {
    if (Number(number) === 1) {
      return 'pertama';
    }
    return `ke-${number}`;
  }
  return '';
}

/**
 * Example:
 * - englishOrginalNumbering(1), return "1st".
 * - englishOrginalNumbering(2), return "2nd".
 * - englishOrginalNumbering(73), return "73rd".
 * - englishOrginalNumbering(1000), return "1000th".
 * @param {Int} number
 */
export function englishOrdinalNumbering(number) {
  const englishOrdinalRules = new Intl.PluralRules('en', {
    type: 'ordinal'
  });
  const suffixes = {
    one: 'st',
    two: 'nd',
    few: 'rd',
    other: 'th'
  };
  const suffix = suffixes[englishOrdinalRules.select(number)];
  return (number + suffix);
}

/**
 * Parse JSON string without worrying about error exception
 * @param {String} jsonString
 */
export const safeJSONParse = jsonString => {
  try {
    return JSON.parse(jsonString);
  } catch {
    return {};
  }
};

/**
 * Format phone number with specified country code
 * @param {number} phoneNumber
 * @param {String} countryCode
 */
export const phoneNumberWithCountryCode = (phoneNumber, countryCode = '+62') => {
  let formattedPhoneNumber = phoneNumber && phoneNumber.replace(/^0/, '+62');
  formattedPhoneNumber = formattedPhoneNumber && formattedPhoneNumber.replace(/^6/, '+6');
  return (formattedPhoneNumber && !formattedPhoneNumber.startsWith('+'))
    ? `${countryCode}${formattedPhoneNumber}` : formattedPhoneNumber;
};

/**
 * Normalize phone number with specified country code
 * @param {number} phoneNumber example: +6281234569
 * @param {String} countryCode
 * @returns {String} example: 081334924932
 */
export const normalizePhoneNumberWithCountryCode = (phoneNumber, countryCode = '+62') => (
  phoneNumber
    ? String(phoneNumber).replace(countryCode, '0')
    : ''
);

/**
 * Get google drive file unique ID from shared link
 * @param {String} originalUrl
 * @returns {String}
 */
export const getGdriveFileId = originalUrl => {
  const urlObject = new URL(originalUrl);
  const pathnameList = urlObject.pathname.split('/');

  return pathnameList[1] === 'open'
    ? urlObject.searchParams.get('id')
    : pathnameList[3];
};

/**
 * Transform google drive file URL to standarized preview url format
 * @param {String} originalUrl
 * @returns {String}
 */
export const transformGdriveFilePreviewUrl = originalUrl => {
  const urlObject = new URL(originalUrl);
  const fileId = getGdriveFileId(originalUrl);
  const newPathname = ['', 'file', 'd', fileId, 'preview'].join('/');

  return `${urlObject.origin}${newPathname}`;
};

/**
 * Analyze each file type
 * @param {String} url
 * @returns {("IMAGE" | "GDRIVE_FILE" | "GDRIVE_FOLDER")}
 */
export const getFileUrlType = url => {
  const urlObject = new URL(url);
  if (urlObject.origin === 'https://drive.google.com') {
    return urlObject.pathname.split('/')[2] === 'folders'
      ? FILE_PREVIEW_TYPE.GDRIVE_FOLDER
      : FILE_PREVIEW_TYPE.GDRIVE_FILE;
  }

  return urlObject.pathname.split('.').pop() === 'pdf'
    ? FILE_PREVIEW_TYPE.PDF
    : FILE_PREVIEW_TYPE.IMAGE;
};

/**
 * Transform google drive file URL to standarized preview url format
 * @param {String} originalUrl
 * @returns {String}
 */
export const transformGdriveFileDownloadUrl = originalUrl => {
  const urlObject = new URL(originalUrl);
  const fileId = getGdriveFileId(originalUrl);

  return `${urlObject.origin}/uc?export=download&id=${fileId}`;
};

/**
 * Transform google drive folder URL to standarized preview url format
 * @param {String} originalUrl
 * @returns {String}
 */
export const transformGdriveFolderPreviewUrl = originalUrl => {
  const urlObject = new URL(originalUrl);
  const pathnameList = urlObject.pathname.split('/');
  const folderId = pathnameList[3];

  return `${urlObject.origin}/embeddedfolderview?id=${folderId}#grid`;
};

/**
 * Merge an option item into existing option list
 * @param {Object[]} baseOptions option list
 * @param {String} baseOptions.value
 * @param {String} baseOptions.label
 * @param {Object} merge option item
 * @param {String} merge.value
 * @param {String} merge.label
 */
export const mergeOptionList = (baseOptions, merge) => (
  merge && merge.value && baseOptions.findIndex(opt => opt.value === merge.value) < 0
    ? [...baseOptions, merge]
    : baseOptions
);

/**
 * Add space formatting for license plate
 * @param {String} licensePlate
 */
export const formatLicensePlate = licensePlate => {
  if (licensePlate) {
    return licensePlate
      .replace(/ /g, '')
      .replace(/(1M4U|G1M|T1M|K1M|[a-zA-Z]+)(\d{1,4})([a-zA-Z]*)/g, '$1 $2 $3')
      .trim()
      .toUpperCase();
  }
  return '';
};

export const getS3FileName = s3Url => {
  const url = new URL(s3Url);
  const splits = url.pathname.split('/');
  const ksuidFileName = splits[splits.length - 1];
  const encoded = ksuidFileName.slice(ksuidFileName.indexOf('-') + 1);
  return decodeURIComponent(encoded);
};

export const secondsToHms = (secondsInput, lang = 'id', abbreviation = 'shortForms') => {
  const secondsNumber = Number(secondsInput);
  const translations = {
    fullForms: {
      d: ' hari',
      h: ' jam',
      m: ' menit',
      s: ' detik',
    },
    shortForms: {
      d: 'h',
      h: 'j',
      m: 'm',
      s: 'd',
    }
  };
  if (!secondsNumber) {
    return '';
  }
  const translatedDays = lang === 'id' ? translations[abbreviation].d : 'd';
  const translatedHours = lang === 'id' ? translations[abbreviation].h : 'h';
  const translatedMinutes = lang === 'id' ? translations[abbreviation].m : 'm';
  const translatedSeconds = lang === 'id' ? translations[abbreviation].s : 's';

  const days = Math.floor(secondsNumber / TOTAL_SECONDS_IN_DAY);
  const hours = Math.floor(
    (secondsNumber % TOTAL_SECONDS_IN_DAY) / TOTAL_SECONDS_IN_HOUR
  );
  const minutes = Math.floor(
    (secondsNumber % TOTAL_SECONDS_IN_HOUR) / TOTAL_MINUTE_IN_HOUR
  );
  const seconds = Math.floor(secondsNumber % TOTAL_MINUTE_IN_HOUR);

  const dDisplay = days > 0 ? `${days}${translatedDays}` : '';
  const hDisplay = hours > 0 || (days > 0 && minutes === 0 && seconds === 0)
    ? `${hours}${translatedHours}`
    : '';
  const mDisplay = minutes > 0
    || (hours > 0 && seconds === 0)
    || (days === 0 && hours === 0 && minutes === 0)
    ? `${minutes}${translatedMinutes}`
    : '';
  const sDisplay = seconds > 0 || (days === 0 && hours === 0 && minutes > 0)
    ? `${seconds}${translatedSeconds}`
    : '';

  return [dDisplay, hDisplay, mDisplay, sDisplay].filter(Boolean).slice(0, 2).join(' ');
};

export const getKiloMeters = distance => {
  if (!distance) {
    return '';
  }

  return `${Math.round(distance * 0.001)} km`;
};

export const getDofFirstDisbursementAmount = ({
  dofKargoFee,
  dofVatKargoFee,
  dofApprovedAmount,
  shipmentBudget
}) => {
  const kargoFeePercentage = numeral(dofKargoFee || 0)
    .multiply(100)
    .divide(dofApprovedAmount)
    .value();
  const vatKargoFeePercentage = numeral(dofVatKargoFee || 0)
    .multiply(100)
    .divide(dofKargoFee)
    .value();
  const kargoFee = numeral(
    numeral(shipmentBudget)
      .multiply(kargoFeePercentage || 0)
      .divide(100)
      .format('0')
  ).value();
  const vatKargoFee = numeral(
    numeral(kargoFee || 0)
      .multiply(vatKargoFeePercentage || 0)
      .divide(100)
      .format('0')
  ).value();

  return (shipmentBudget / 2) - kargoFee - vatKargoFee;
};

export const capitalize = str => {
  if (isEmpty(str)) {
    return str;
  }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const getDoType = (shipmentDetails, t) => {
  let originType = get(shipmentDetails, get(shipmentDetails, 'job')
    ? 'job.origin.type'
    : 'origin.type');
  let destinationType = get(shipmentDetails, get(shipmentDetails, 'job')
    ? 'job.destination.type'
    : 'destination.type');
  originType = originType ? t(`common:location_type.${originType}`) : '';
  destinationType = destinationType ? t(`common:location_type.${destinationType}`) : '';
  return `${originType} - ${destinationType}`;
};

export const groupShipmentStatusTracker = statusTrackers => (Array.isArray(statusTrackers)
  ? statusTrackers.reduce((accumulator, tracker) => {
    if (get(tracker, 'status') === SHIPMENT_STATUSES.PLANNED) {
      return accumulator;
    }
    const date = moment(get(tracker, 'timestamp')).format('YYYY-MM-DD');
    const existingDateIndex = accumulator.findIndex(
      acc => acc.date === date
    );
    return existingDateIndex > -1
      ? Object.assign([], accumulator, {
        [existingDateIndex]: {
          ...accumulator[existingDateIndex],
          logs: [
            ...accumulator[existingDateIndex].logs,
            {
              key: Math.random().toString(36).substring(7),
              status: get(tracker, 'status'),
              timestamp: get(tracker, 'timestamp'),
              updatedBy: get(tracker, 'updatedBy'),
              updatedFrom: get(tracker, 'updatedFrom')
            }
          ]
        }
      })
      : [
        ...accumulator,
        {
          date,
          logs: [
            {
              key: Math.random().toString(36).substring(7),
              status: get(tracker, 'status'),
              timestamp: get(tracker, 'timestamp'),
              updatedBy: get(tracker, 'updatedBy'),
              updatedFrom: get(tracker, 'updatedFrom')
            }
          ]
        }
      ];
  }, [])
  : []);

/**
 * Returns number with thousand separator
 * @param {number} number
 */
export function formatThousandSeparator(number) {
  if (number === undefined || number === null || number === '' || number === '-') return number;
  return numeral(number).format('0,0.[0000000000]');
}

/**
 * Returns number form any format
 * @param {number} number
 */
export function parseToNumber(number) {
  if (number === '-') return number;
  return numeral(number).value();
}

export const dateToIsoString = (date, defaultVal = undefined) => (
  date && moment(date).isValid()
    ? moment(date).startOf('day').toISOString()
    : defaultVal
);

export const formatDate = (date, defaultVal = undefined, format = 'YYYY-MM-DD') => (
  date && moment(date).isValid()
    ? moment(date).format(format)
    : defaultVal
);

export const formatDateWIB = (date = null, format = 'D MMMM YYYY, HH:mm') => (
  `${moment(date).format(format)} WIB`
);

/**
 * This is to convert snake_case to Title Case
 * @param {string} str that is in snake_case
 * @returns {string} the formatted string in Title Case
 */
export const snakeCaseToTitleCase = snakeCaseString => {
  const removedUnderscore = String(snakeCaseString || '').split('_').join(' ');
  return startCase(removedUnderscore);
};

/**
 * This is to convert number into roman number
 * @param {number} num that is integer
 * @returns {string} the roman format of the number
 */
export const convertToRoman = num => {
  const numberArr = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const RomanArr = [
    'M',
    'CM',
    'D',
    'CD',
    'C',
    'XC',
    'L',
    'XL',
    'X',
    'IX',
    'V',
    'IV',
    'I'
  ];
  const result = [];

  const findElement = e => e <= num;

  while (num > 0) {
    const nextHighest = numberArr.find(findElement);

    result.push(RomanArr[numberArr.indexOf(nextHighest)]);
    // eslint-disable-next-line no-param-reassign
    num -= nextHighest;
  }
  const newResult = result.join('');

  return newResult;
};

export default {};
